import React, { useEffect, useState } from 'react';
import { FlatList, ActivityIndicator, Platform } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { view_styles } from '../constants/styles';
import { Icons } from '../Components';
import { ShareApi, ShareHelpers } from './api';
import type { BEEventProps, GeneratedLinkProps, PublicPlayerProps } from '../types';
import EmbedPreview from './components/EmbedPreview';
import { useColors } from '../constants/useColors';
import { Button, Text, TextInput, View } from '../Components/Themed';
import { useContacts } from '../SocialComponents/Contacts/useContacts';



type ShareWidgetProps = {
    redirect_url?:string,
    generated_link_id?:string,
    title:string,
    body?:string,
    player_id?:string,
    generator: 'player'|'company'|'tool',
    link_type: GeneratedLinkProps['link_type'],
    link_sub_type: GeneratedLinkProps['link_sub_type'],
    type_id?:string,
    embed?:{
        embed_code:string,
        script_source?:string,
        widget_element?:any,
        div_id?:string
    },
    onCancel:() => void,
    onShare?:(be_event:BEEventProps) => void,
    onSelectContact:(data:{ player:PublicPlayerProps,number:string }) => void,
    allow_edit?:boolean,
    maxWidth?:number
    maxHeight?:number
}

const share_sections = ['header','view_link','edit_link','view_embed','footer']

const ShareWidget = ({ generated_link_id, title, body, redirect_url, generator, allow_edit, player_id, link_type, link_sub_type, type_id, embed, onCancel, onShare, onSelectContact }:ShareWidgetProps) => {
    const Colors = useColors();
    const Contacts = useContacts({});
    const [ preview_embed, setPreviewEmbed ] = useState(false);
    const [ link_height, setLinkHeight ] = useState(0);
    const [ active_tab, setActiveTab ] = useState('link');
    const [ link_status, setLinkStatus ] = useState<{
        loading: boolean,
        update_loading:boolean,
        native_share_allowed?:boolean,
        editing:boolean,
        generated_link?:GeneratedLinkProps,
        draft_generated_link?:GeneratedLinkProps,
        visible_id_valid:Boolean,
        query_params:{ key:string, value:string }[]
        url?:string
    }>({
        loading: false,
        update_loading: false,
        native_share_allowed:false,
        editing:false,
        visible_id_valid: true,
        query_params: []
    });
    const { loading, editing, generated_link, draft_generated_link, url, visible_id_valid, update_loading, native_share_allowed } = link_status;

    const native_share_options = [
        { name: 'twitter', getIcon: () => <View style={{ backgroundColor:Colors.text.white, padding:2, borderRadius:2 }}><Icons.TwitterXIcon size={18}/></View> },
        { name: 'instagram', getIcon: () => <Icons.InstagramIcon size={18}/> },
        { name: 'facebook', getIcon:() => <Icons.FacebookIcon size={18} /> },
        { name: 'more', getIcon:() => <Icons.MoreIcon size={18} color={Colors.text.h1} /> }
    ]

    useEffect(() => {
        if(!redirect_url && !generated_link_id){ return }//Unable to get redirect link
        ShareApi.setEnvironment();
        if(generated_link_id){ getGeneratedLink(generated_link_id) }
        else if(redirect_url){ generateLink(redirect_url) }
    },[redirect_url, generated_link_id]);


    const getGeneratedLink = async(generated_link_id:string) => {
        setLinkStatus({ ...link_status, loading:true });
        const retrieved_link = await ShareApi.getGeneratedLinkById(generated_link_id);
        const nsa = await ShareHelpers.nativeShareAvailable()
        setLinkStatus({
            ...link_status,
            loading:false,
            native_share_allowed: nsa,
            generated_link:retrieved_link?.generated_link,
            draft_generated_link: retrieved_link?.generated_link,
            query_params: ShareHelpers.extractQueryParams(retrieved_link?.generated_link.redirect_url),
            url: retrieved_link?.url
        });
    }


    const generateLink = async(redirect_url:string) => {
        setLinkStatus({ ...link_status, loading: true });
        //If there is a player id - lets append the referral code if one is not supplied!
        if(player_id){ redirect_url = await ShareHelpers.appendPlayerReferral(redirect_url) }
        const nsa = await ShareHelpers.nativeShareAvailable()
        const { generated_link, url } = await ShareApi.generateShortLink({
            generator: generator,
            generated_link_id:'',
            generator_id: player_id,
            type_id,
            redirect_url,
            link_type,
            link_sub_type
        });

        setLinkStatus({
            ...link_status,
            loading: false,
            generated_link,
            native_share_allowed: nsa,
            draft_generated_link: generated_link,
            url
        });
    }

    const handleSelectContact = async():Promise<void> => {
        if(!Contacts.is_available){ return }
        if(!Contacts.permission){ return }
        if(!Contacts.permission.granted){
            if(Contacts.permission.canAskAgain){ 
                Contacts.requestPermissions();
                return
            }
            return //We dont have access!!
        }

        //Ok! Contacts is granted.  Lets select a user
        const response = await Contacts.getContact();
        if(!response){ return } // Sorry we failed
        const { player, number } = response;
        onSelectContact({ player, number })
    }


    const renderNativeOptions = (data:{ item:any, index:number }) => {
        return (
            <View transparent style={{ paddingRight:5 }}>
                {data.item.getIcon()}
            </View>
        )
    }

    const renderSections = (data:{item:string, index:number}) => {
        switch(data.item){
            case 'header':
                return (
                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:20, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                        <View transparent style={{ flex:1 }}>
                            <Text theme='h1'>Share Modal</Text>
                            <Text style={{ marginTop:3 }} theme='description'>{title}</Text>
                        </View>
                        <Button
                            title='X'
                            type='close'
                            style={{ padding:10 }}
                            onPress={() => onCancel()}
                        />
                    </View>
                )
                case 'edit_link':
                    if(!editing || !draft_generated_link){ return <></> }
                    return (
                        <View transparent>
                            <View transparent type='row' style={{ padding:20 }}>
                                <View transparent style={{ flex:1 }}>
                                    <Text size={16} theme='description' weight='regular'>Edit This Link</Text>
                                    <Text style={{ marginTop:3 }} theme='description'>Edit the visible Id and query parameters below</Text>
                                </View>
                            </View>
                            <View transparent style={{ padding:10 }}>
                                <View transparent type='row' style={{ padding:10 }}>
                                    <View transparent style={{ flex:1 }}>
                                        <Text theme='h2'>Short Link</Text>
                                        <View transparent style={{ flexDirection:'row', alignItems:'center' }}>
                                            <Text style={{ marginTop:3 }} theme='description'>https://share.mybe.app/id/</Text>
                                            <TextInput
                                                value={draft_generated_link.visible_id}
                                                style={{ flex:1, maxWidth:150 }}
                                                onChangeText={(text) => {
                                                    setLinkStatus({
                                                        ...link_status,
                                                        draft_generated_link: { ...draft_generated_link, visible_id: text },
                                                        visible_id_valid: false
                                                    })
                                                }}
                                            />
                                        </View>
                                    </View>
                                </View>
                                <View transparent type='row' style={{ padding:10 }}>
                                    <View transparent style={{ flex:1 }}>
                                        <Text theme='h1'>Link Destination</Text>
                                        <TextInput
                                            value={draft_generated_link.redirect_url}
                                            multiline={true}
                                            style={{ 
                                                marginTop:4,
                                                flex:1, 
                                                height: Math.max(link_height, 35) }}
                                                onContentSizeChange={(ev) => {
                                                    setLinkHeight(ev.nativeEvent.contentSize.height)
                                            }}
                                            onChangeText={(text) => {
                                                let new_params = ShareHelpers.extractQueryParams(text);
                                                setLinkStatus({
                                                    ...link_status,
                                                    draft_generated_link: { ...draft_generated_link, redirect_url: text },
                                                    query_params: new_params
                                                })
                                            }}
                                        />
                                    </View>
                                </View>
                            </View>
                            <View type='footer' style={{ padding:10 }}>
                                <Button
                                    title='Cancel'
                                    type='error'
                                    style={{ marginBottom:10 }}
                                    onPress={() => setLinkStatus({ ...link_status, editing: false, draft_generated_link: generated_link })}
                                />
                                <View transparent style={{ flex:1 }}/>
                                {needs_verification ?
                                <Button
                                    float
                                    title='Check Link Availability'
                                    style={{ opacity:update_loading?0.5:1 }}
                                    loading={update_loading}
                                    type='info'
                                    onPress={async() => {
                                        setLinkStatus({ ...link_status, update_loading: true })
                                        const is_available = await ShareApi.isGeneratedLinkAvailable(draft_generated_link);
                                        if(!is_available){ 
                                            setLinkStatus({ ...link_status, update_loading: false })
                                            return alert('Sorry! This link is not available.  Please change the visible id portion of the link and try again') 
                                        }
                                        setLinkStatus({
                                            ...link_status,
                                            update_loading:false,
                                            visible_id_valid: true
                                        })
                                    }}
                                />
                                :
                                <Button
                                    title='Save'
                                    style={{ opacity:link_is_changed && !update_loading?1:0.5 }}
                                    disabled={!link_is_changed}
                                    loading={update_loading}
                                    type='success'
                                    onPress={async() => {
                                        setLinkStatus({ ...link_status, update_loading: true });
                                        const new_link = await ShareApi.updateGeneratedLink(draft_generated_link);
                                        if(!new_link?.generated_link){ return alert('Unable to update link') }
                                        setLinkStatus({
                                            ...link_status,
                                            update_loading: false,
                                            editing: false,
                                            generated_link: new_link?.generated_link,
                                            draft_generated_link: new_link?.generated_link,
                                            url: new_link?.url
                                        });
                                    }}
                                />
                                }
                            </View>
                        </View>
                    )
                case 'view_link':
                    if(!generated_link || active_tab != 'link'){ return <></> }
                    if(editing){ return <></> }
                    return (
                        <View style={{ ...view_styles.section_body }}>
                            <View style={{ paddingBottom:0 }}>
                                <View style={{ ...view_styles.body_row }}>
                                    <Text size={16} color={Colors.text.h1} weight='regular'>Share this link via</Text>
                                </View>
                                <View style={{ flexDirection:'row', alignItems:'center', marginBottom:10 }}>
                                {native_share_allowed ? 
                                <Button float style={{ flex:1, flexDirection:'row', alignItems:'center', padding:10, marginRight:5 }} onPress={async() => {
                                    const share_event = await ShareApi.share(title, body, link_status.url, generated_link.generated_link_id);
                                    if(onShare && share_event){ onShare(share_event) }
                                }}>     <View style={{ marginRight:10 }}>
                                            <Icons.ShareIcon size={19} color={Colors.text.h1} />
                                        </View>
                                        <FlatList
                                            horizontal
                                            data={native_share_options}
                                            keyExtractor={(item) => item.name}
                                            renderItem={renderNativeOptions}
                                        />
                                </Button>
                                :<></>}
                                {Platform.OS != 'web' && Contacts.is_available ?
                                    <Button float style={{ flex:1, flexDirection:'row', alignItems:'center', padding:10, marginLeft:5 }} onPress={() => handleSelectContact()}>
                                        <Icons.ContactsIcon size={24} color={Colors.text.h1} />
                                        <Text style={{ marginLeft:5 }} theme='h2'>Select Contact</Text>
                                    </Button>
                                :<></>}
                                </View>
                            </View>
                            <View style={{ paddingTop:0, }}>
                                <View style={{ ...view_styles.body_row }}>
                                    <Text selectable size={16} color={Colors.text.h1} weight='regular'>Copy link</Text>
                                </View>
                                <View type='row' style={{ padding:10 }}>
                                    {url ?
                                    <Button float type='info' style={{ flexDirection:'row', alignItems:'center', borderRadius:4 }} onPress={() => Clipboard.setStringAsync(url)}>
                                        <Icons.LinkIcon size={18} color={Colors.text.h1} />
                                        <Text style={{ marginLeft:4 }} size={12} color={Colors.text.h1} textAlign='left'>Copy</Text>
                                    </Button>
                                    :<></>}
                                    <Text style={{ marginLeft:5, flex:1 }} theme='description'>{url}</Text>
                                    {allow_edit ?
                                    <Button style={{ flexDirection:'row', alignItems:'center', padding:10, borderRadius:4 }} onPress={() => setLinkStatus({ ...link_status, editing: true })}>
                                        <Icons.EditIcon size={18} color={Colors.text.h1} />
                                    </Button>
                                    :<></>}
                                </View>
                            </View>
                        </View>
                    )
                case 'view_embed':
                    if(!embed || active_tab == 'link'){ return <></> }
                    return (
                        <View>
                        <View transparent type='row' style={{ padding:10 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text size={16} theme='description'>Embed this in your website</Text>
                                <Text style={{ marginTop:3 }} theme='light'>Copy and past the code snippet below into the 'body' tag</Text>
                            </View>
                        </View>
                        <View transparent style={{ padding:10 }}>
                            <Text selectable size={14} theme='description'>{embed.embed_code}</Text>
                            <View type='row' transparent style={{ padding:5 }}>
                                <Button float style={{ flexDirection:'row', alignItems:'center' }}
                                onPress={() => Clipboard.setStringAsync(embed.embed_code)}>
                                    <Icons.LinkIcon size={18} color={Colors.text.h1} />
                                    <Text style={{ marginLeft:4 }} size={12} color={Colors.text.h1} textAlign='left'>Copy</Text>
                                </Button>
                                <View transparent style={{ flex:1 }} />
                                {embed.widget_element && embed.script_source && embed.div_id && Platform.OS == 'web' ?
                                <Button
                                    title={preview_embed ? 'Hide Preview' : 'Show Preview'}
                                    title_color={Colors.text.white}
                                    disabled={Platform.OS != 'web' ? true : false}
                                    backgroundColor={preview_embed ? Colors.text.error : Colors.text.action}
                                    onPress={() => setPreviewEmbed(!preview_embed)}
                                />
                                :<></>}
                            </View>
                        </View>
                        
                        {preview_embed && embed.widget_element && embed.script_source && embed.div_id ?
                        <View style={{ borderWidth:1, borderRadius:4 }}>
                            <EmbedPreview
                                script_source={embed.script_source}
                                widget_element={embed.widget_element}
                                div_id={embed.div_id}
                            />
                        </View>
                        :<></>}
                    </View>
                    )
                case 'footer':
                    return (
                        <View type='footer' style={{ borderBottomRightRadius:8, borderBottomLeftRadius:8, padding:2 }}>
                            {active_tab == 'embed' ?
                            <Button transparent style={{ flexDirection:'row', alignItems:'center' }} onPress={() => setActiveTab('link')}>
                                <View transparent style={{ flex:1, marginRight:10 }}>
                                    <Text theme='h1'>Share Link Options</Text>
                                    <Text style={{ marginTop:3 }} theme='description'>Customize the link and share</Text>
                                </View>
                                <View transparent style={{ height:30, width:30, borderRadius:100, justifyContent:'center', alignItems:'center' }}>
                                    <Icons.LinkIcon color={Colors.text.h1} size={18}/>
                                </View>
                            </Button>
                            : active_tab == 'link' && embed?.embed_code ?
                            <Button transparent style={{ flexDirection:'row', alignItems:'center' }} onPress={() => setActiveTab('embed')}>
                                <View transparent style={{ flex:1, marginRight:10 }}>
                                    <Text theme='h1'>Available For Embed</Text>
                                    <Text style={{ marginTop:3 }} theme='description'>Copy code snippet and paste into the html of your website</Text>
                                </View>
                                <View transparent style={{ height:30, width:30, borderRadius:100, justifyContent:'center', alignItems:'center' }}>
                                    <Icons.EmbedIcon color={Colors.text.h1} size={18}/>
                                </View>
                            </Button>
                            :<></>}
                        </View>
                    )
            default: return <></>
        }
    }

    const link_is_changed = JSON.stringify(draft_generated_link) != JSON.stringify(generated_link)
    const needs_verification = link_is_changed && !visible_id_valid
    return (
        <View float>
            <FlatList
                data={share_sections}
                renderItem={renderSections}
                keyExtractor={(item) => item}
                key={'share_section_list'}
            />
            {loading ?
            <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, alignItems:'center', padding:20 }}>
                <ActivityIndicator color={Colors.text.h1} size={'large'} />
            </View>
            :<></>}
        </View>
    )
}

export default ShareWidget